import { type FC, useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import debounce from 'lodash/debounce.js';
import { trackEvent, eventNameConstants } from 'dibs-tracking';

import { SharedPaginationComponent } from './SharedPaginationComponent.tsx';

import { updateUriRef } from '../../actions/filterActions.ts';
import {
    PAGINATION_TOTAL_LINKS_DESKTOP,
    PAGINATION_TOTAL_LINKS_TABLET_LANDSCAPE,
    PAGINATION_TOTAL_LINKS_TABLET_PORTRAIT,
    WIDTH_DESKTOP,
    WIDTH_TABLET_LANDSCAPE,
} from './sharedPaginationConstants.ts';
import { type OnLinkClick } from './SharedPaginationTypes.ts';

type Props = {
    currentPage: number;
    numberOfPages: number;
    displayUriRef: string;
    hideHref?: boolean;
};

export const SharedPagination: FC<Props> = ({
    currentPage,
    numberOfPages,
    displayUriRef,
    hideHref,
}) => {
    const dispatch = useDispatch();
    const [totalPageLinks, setTotalPageLinks] = useState(PAGINATION_TOTAL_LINKS_DESKTOP);

    const getTotalPageLinks = useCallback(() => {
        const width = window.innerWidth;
        let links = PAGINATION_TOTAL_LINKS_DESKTOP;

        if (width <= WIDTH_TABLET_LANDSCAPE) {
            links = PAGINATION_TOTAL_LINKS_TABLET_PORTRAIT;
        } else if (width <= WIDTH_DESKTOP) {
            links = PAGINATION_TOTAL_LINKS_TABLET_LANDSCAPE;
        }

        return links;
    }, []);

    useEffect(() => {
        setTotalPageLinks(getTotalPageLinks());
    }, [getTotalPageLinks]);

    const onResize = useMemo(
        () => debounce(() => setTotalPageLinks(getTotalPageLinks()), 500, { leading: true }),
        [getTotalPageLinks]
    );

    useEffect(() => {
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            onResize.cancel();
        };
    }, [onResize]);

    const onLinkClick: OnLinkClick = useCallback(
        ({ e, href, pageNumber }) => {
            e.preventDefault();
            dispatch(updateUriRef({ uriRef: href }));
            window.scroll(0, 0);
            const action = 'pagination page number clicked';
            const label = `${pageNumber}/${numberOfPages}`;
            trackEvent(
                {
                    eventName: eventNameConstants.EVENT_RESULTS_PAGE_REFINEMENT,
                    category: 'navigation',
                    action,
                    label,
                    interaction_type: action,
                    step_interaction_name: label,
                },
                e
            );
        },
        [dispatch, numberOfPages]
    );

    return (
        <SharedPaginationComponent
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            uriRef={displayUriRef}
            totalPageLinks={totalPageLinks}
            onLinkClick={onLinkClick}
            hideHref={hideHref}
        />
    );
};
